import { useMemo } from 'react';
import { BarChart3, Calendar, Tag, TrendingUp } from 'lucide-react';

import { CATEGORY_LABEL_KEYS } from '../lib/categories';
import { isLowQualityPhoto } from '../lib/findings';
import { t, type UiLangCode } from '../lib/i18n';
import type { CategoryCode, Cents, ReceiptSession } from '../lib/types';
import { CollapsibleSection } from './CollapsibleSection';

interface BatchSummaryProps {
  sessions: readonly ReceiptSession[];
  lang: UiLangCode;
}

/**
 * Post-batch summary card. Aggregates the user's processed receipts by
 * category, top vendors (by total spend), and month — the three views a
 * bookkeeping user wants once a batch is parsed. Excludes low-quality
 * photos from every aggregate because their parsed totals can't be
 * trusted (mirrors the same exclusion in BatchProgress's grand total +
 * the xlsx/zip exports).
 *
 * Multi-currency batches: each (category, currency) and (vendor, currency)
 * pair is its own bucket so cross-currency sums never get silently mixed.
 * Same convention the export workbook uses.
 *
 * Only shown when there are at least 2 successful receipts — a single-
 * receipt "summary" is degenerate (three rows that each say the same
 * thing as the session card above).
 */

type CategoryKey = CategoryCode | 'uncategorised';

interface CategoryBucket {
  category: CategoryKey;
  count: number;
  total: Cents;
  currency: string;
}

interface VendorBucket {
  vendor: string;
  count: number;
  total: Cents;
  currency: string;
}

interface MonthBucket {
  month: string; // YYYY-MM
  count: number;
}

const TOP_VENDORS_LIMIT = 5;

function aggregate(sessions: readonly ReceiptSession[]): {
  byCategory: CategoryBucket[];
  topVendors: VendorBucket[];
  byMonth: MonthBucket[];
  successCount: number;
} {
  const cats = new Map<string, CategoryBucket>();
  const vendors = new Map<string, VendorBucket>();
  const months = new Map<string, number>();
  let successCount = 0;

  for (const s of sessions) {
    if (s.state.kind !== 'success' || s.receipt === null) continue;
    if (isLowQualityPhoto(s)) continue;
    successCount++;

    const r = s.receipt;
    const ccy = r.currency.value;
    const cat: CategoryKey = r.category ?? 'uncategorised';
    const catKey = `${cat}|${ccy}`;
    const existingCat = cats.get(catKey);
    if (existingCat !== undefined) {
      existingCat.count += 1;
      existingCat.total += r.total.value;
    } else {
      cats.set(catKey, {
        category: cat,
        count: 1,
        total: r.total.value,
        currency: ccy,
      });
    }

    const vendorRaw = r.vendor.value.trim();
    if (vendorRaw !== '') {
      const vendorLower = vendorRaw.toLowerCase();
      const vKey = `${vendorLower}|${ccy}`;
      const existingV = vendors.get(vKey);
      if (existingV !== undefined) {
        existingV.count += 1;
        existingV.total += r.total.value;
      } else {
        vendors.set(vKey, {
          vendor: vendorRaw,
          count: 1,
          total: r.total.value,
          currency: ccy,
        });
      }
    }

    const month = r.date.value.toISOString().slice(0, 7);
    months.set(month, (months.get(month) ?? 0) + 1);
  }

  const byCategory = [...cats.values()].sort((a, b) => b.total - a.total);
  const topVendors = [...vendors.values()]
    .sort((a, b) => b.total - a.total)
    .slice(0, TOP_VENDORS_LIMIT);
  const byMonth = [...months.entries()]
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([month, count]) => ({ month, count }));

  return { byCategory, topVendors, byMonth, successCount };
}

function formatCents(c: Cents): string {
  return (c / 100).toFixed(2);
}

function categoryLabel(lang: UiLangCode, key: CategoryKey): string {
  if (key === 'uncategorised') return t(lang, 'category.placeholder');
  return t(lang, CATEGORY_LABEL_KEYS[key]);
}

/**
 * Render YYYY-MM as the localised "long-month YYYY". Falls back to the
 * raw "YYYY-MM" string if Intl barfs on the locale (shouldn't, but the
 * fallback keeps the panel rendering even if locale data is patchy).
 */
function formatMonth(month: string, lang: UiLangCode): string {
  const [y, m] = month.split('-');
  if (y === undefined || m === undefined) return month;
  const d = new Date(Number(y), Number(m) - 1, 1);
  if (Number.isNaN(d.getTime())) return month;
  try {
    return new Intl.DateTimeFormat(lang.slice(0, 2), {
      month: 'long',
      year: 'numeric',
    }).format(d);
  } catch {
    return month;
  }
}

function MoneyCell({ cents, currency }: { cents: Cents; currency: string }) {
  return (
    <span className="font-semibold tabular-nums text-slate-800 dark:text-slate-100">
      {formatCents(cents)}{' '}
      <span className="text-xs font-medium text-slate-500 dark:text-slate-400">
        {currency}
      </span>
    </span>
  );
}

export function BatchSummary({ sessions, lang }: BatchSummaryProps) {
  const data = useMemo(() => aggregate(sessions), [sessions]);
  if (data.successCount < 2) return null;

  return (
    <div className="rounded-xl border border-slate-200 bg-white shadow-sm dark:border-slate-800 dark:bg-slate-900">
      <CollapsibleSection
        defaultOpen
        triggerClassName="select-none px-4 py-3 hover:bg-slate-50 rounded-xl dark:hover:bg-slate-800/50"
        trigger={
          <span className="flex items-center gap-2">
            <BarChart3
              className="h-4 w-4 text-slate-500 dark:text-slate-400"
              aria-hidden="true"
            />
            <span className="text-sm font-medium text-slate-800 dark:text-slate-100">
              {t(lang, 'summary.title')}
            </span>
          </span>
        }
      >
        <div className="grid gap-4 border-t border-slate-200 px-4 py-3 sm:grid-cols-3 dark:border-slate-800">
        <section aria-labelledby={`summary-cat-${lang}`}>
          <h3
            id={`summary-cat-${lang}`}
            className="mb-2 flex items-center gap-1.5 text-xs font-medium uppercase tracking-wide text-slate-500 dark:text-slate-400"
          >
            <Tag className="h-3 w-3" aria-hidden="true" />
            {t(lang, 'summary.byCategory')}
          </h3>
          <ul className="space-y-1 text-sm">
            {data.byCategory.map((b) => (
              <li
                key={`${b.category}|${b.currency}`}
                className="flex items-center justify-between gap-2"
              >
                <span className="truncate text-slate-700 dark:text-slate-300">
                  {categoryLabel(lang, b.category)}
                </span>
                <MoneyCell cents={b.total} currency={b.currency} />
              </li>
            ))}
          </ul>
        </section>

        <section aria-labelledby={`summary-vendors-${lang}`}>
          <h3
            id={`summary-vendors-${lang}`}
            className="mb-2 flex items-center gap-1.5 text-xs font-medium uppercase tracking-wide text-slate-500 dark:text-slate-400"
          >
            <TrendingUp className="h-3 w-3" aria-hidden="true" />
            {t(lang, 'summary.topVendors')}
          </h3>
          <ul className="space-y-1 text-sm">
            {data.topVendors.map((v) => (
              <li
                key={`${v.vendor}|${v.currency}`}
                className="flex items-center justify-between gap-2"
              >
                <span className="truncate text-slate-700 dark:text-slate-300">
                  {v.vendor}
                  {v.count > 1 && (
                    <span className="ml-1 text-xs text-slate-500 dark:text-slate-400">
                      ×{v.count}
                    </span>
                  )}
                </span>
                <MoneyCell cents={v.total} currency={v.currency} />
              </li>
            ))}
          </ul>
        </section>

        <section aria-labelledby={`summary-months-${lang}`}>
          <h3
            id={`summary-months-${lang}`}
            className="mb-2 flex items-center gap-1.5 text-xs font-medium uppercase tracking-wide text-slate-500 dark:text-slate-400"
          >
            <Calendar className="h-3 w-3" aria-hidden="true" />
            {t(lang, 'summary.byMonth')}
          </h3>
          <ul className="space-y-1 text-sm">
            {data.byMonth.map((m) => (
              <li
                key={m.month}
                className="flex items-center justify-between gap-2"
              >
                <span className="truncate text-slate-700 dark:text-slate-300">
                  {formatMonth(m.month, lang)}
                </span>
                <span className="font-semibold tabular-nums text-slate-800 dark:text-slate-100">
                  {m.count}
                </span>
              </li>
            ))}
          </ul>
        </section>
        </div>
      </CollapsibleSection>
    </div>
  );
}
